import React from "react";
import { Paper, Box, useTheme } from "@mui/material";
import { MoreButton, Text, Image, Video, Heading, Buttons } from "./../";

function Highlight({
	title,
	anchor,
	link,
	buttons,
	text,
	video,
	image,
	orientation,
	fullWidth,
	layout,
	flush,
	underline,
	buttonColor = "secondary",
	buttonStyle = "default",
	style,
}) {
	const is_side_layout = layout === "left" || layout === "right";
	const no_paper_wrap = flush || style === "overlap";

	const theme = useTheme();

	const bg_color = theme.palette.highlight
		? theme.palette.highlight
		: theme.palette.grey;

	const content = (
		<>
			{text && <Text html={text} />}
			{link && (
				<MoreButton
					to={link.to}
					toLabel={link.label}
					fullWidth
					variant="contained"
					size="lg"
					sx={(theme) => ({ marginTop: theme.spacing(2) })}
					color={buttonColor}
					link={buttonStyle === "link"}
				/>
			)}
			{buttons && (
				<Buttons links={buttons} color={buttonColor} type={buttonStyle} />
			)}
		</>
	);

	function Wrapper({ side }) {
		if (!side && style !== "default") {
			side = "left";
		}
		const titleComponent = (
			<Box
				sx={(theme) => ({
					...(side && {
						marginBottom: theme.spacing(3),
					}),
				})}
			>
				<Heading anchor={anchor} headingType="h2" underline={underline}>
					{title}
				</Heading>
			</Box>
		);
		let thumbnail = <></>;
		if (style === "frame" && image) {
			thumbnail = (
				<Box
					className="img"
					style={{
						backgroundImage: `url(${image.src})`,
						backgroundSize: "cover",
						backgroundPosition: "center center",
						width: "33.264%",
						margin: 0,
					}}
					component="figure"
				></Box>
			);
		} else if (video) {
			thumbnail = (
				<Box style={{ width: side ? "50%" : "100%" }}>
					<Video thumbnail={video.thumbnail.url} url={video.url} />
				</Box>
			);
		} else if (image) {
			thumbnail = (
				<Box style={{ width: side ? "50%" : "100%" }}>
					<Image
						src={image.src}
						orientation={orientation}
						href={image.href}
						alt={image.alt}
						caption={image.title}
					/>
				</Box>
			);
		}

		if (!side && style === "default") {
			return (
				<Box
					component={no_paper_wrap ? "div" : Paper}
					className={`highlight style-${style} ${flush ? "flush" : ""}`}
					sx={(theme) => ({
						padding: theme.spacing(no_paper_wrap ? 0 : fullWidth ? 4 : 3),
						backgroundColor: bg_color.light,
						"& .inner > *": {
							marginTop: theme.spacing(3),
							marginBottom: 0,
							"&:first-child": {
								marginTop: 0,
							},
						},
						"& .buttons": {
							marginTop: theme.spacing(3),
						},
					})}
				>
					<Box className="inner">
						{titleComponent}
						{thumbnail}
						{content}
					</Box>
				</Box>
			);
		} else {
			return (
				<Box
					component={no_paper_wrap ? "div" : Paper}
					className={`highlight style-${style} side-${side} ${
						flush ? "flush" : ""
					}`}
					sx={(theme) => ({
						backgroundColor: no_paper_wrap ? "transparent" : bg_color.light,
						display: "flex",
						flexWrap: "wrap",
						...(!no_paper_wrap && { padding: theme.spacing(3) }),
						"& figure.image": {
							marginTop: 0,
							marginBottom: 0,
						},
						"& .buttons": {
							marginTop: theme.spacing(3),
						},
						"&.style-overlap": {
							// alignItems: "center",
							"& .inner": {
								position: "relative",
								zIndex: 3,
							},
							"&.side-left .inner": {
								marginLeft: theme.spacing(-4),
								marginTop: theme.spacing(4),
							},
							"&.side-right .inner": {
								marginRight: theme.spacing(-4),
								marginTop: theme.spacing(-4),
							},
						},
					})}
					elevation={style === "default" ? 3 : 0}
				>
					<>
						{side === "left" && thumbnail}
						<Box
							sx={(theme) => ({
								padding: theme.spacing(no_paper_wrap ? 0 : 3),
								...(style !== "overlap" && {
									paddingLeft: theme.spacing(side === "left" ? 3 : 0),
									paddingRight: theme.spacing(side === "right" ? 3 : 0),
								}),
								...(style === "frame" && {
									paddingRight: theme.spacing(3),
									paddingLeft: theme.spacing(3),
								}),
								width: style === "frame" ? "66.528%" : "50%",
							})}
						>
							<Box
								className="inner"
								sx={{
									...(style === "overlap" && {
										backgroundColor: bg_color.light,
										padding: theme.spacing(10, 8),
									}),
								}}
							>
								{titleComponent}
								{content}
							</Box>
						</Box>
						{side === "right" && thumbnail}
					</>
				</Box>
			);
		}
	}

	return <Wrapper side={is_side_layout ? layout : null}>{content}</Wrapper>;
}

export default Highlight;
